import {
  Button,
  SelectOptionType,
  addToast,
  Select,
  TextInput,
} from '@octano/global-ui';
import React, { useEffect, useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { HTTP_STATUS_CODE } from '../../../../api/request';
import { createPetition } from '../../../../api/requests/petitions';
import { useBase } from '../../../../components/base/BaseProvider';
import { useFetch } from '../../../../components/contexts/FetchContext';
import DisplayError from '../../../../components/info/DisplayError';
import Loading from '../../../../components/info/Loading';
import { TranslationsKeys } from '../../../../locales/translations';
import {
  PaginationQuery,
  PaginationRequestType,
} from '../../../../types/paginationRequestType';
import {
  CreatePetitionData,
  PetitionData,
  PetitionInf,
  PetitionPaymentTypeId,
  PetitionTypes,
} from '../../../../types/petitions';
import { TariffFormatter } from '../../../../utils/currency';
import PetitionFormDefault from './PetitionFormDefault';
import PetitionFormProficiency from './PetitionFormProficiency';
import PetitionFormReplaceCourseGrade from './PetitionFormReplaceCourseGrade';
import PetitionFormReplaceEvaluationGrade from './PetitionFormReplaceEvaluationGrade';
import PetitionFormSubjectAssignment from './PetitionFormSubjectAssignment';
import PetitionFormValidation from './PetitionFormValidation';

export interface CreatePetitionFormProps extends CreatePetitionData {
  close: () => void;
  error?: string;
  loading: boolean;
}
export default function CreatePetitionForm({
  types,
  cellPhone,
  email,
  replaceEvaluationGrades,
  replaceCourseGrades,
  proficiencyCourses,
  subjectAssignmentCourses,
  close,
  error,
  loading,
}: CreatePetitionFormProps) {
  const { t } = useTranslation(TranslationsKeys.PETITIONS);

  const { careers } = useBase();
  const careerOptions = useMemo<SelectOptionType[]>(
    () =>
      careers.map((c) => ({
        value: c.studyPlanEnrollmentId,
        label: c.name,
      })),
    [careers],
  );

  const methods = useForm<PetitionData>({
    mode: 'onChange',
    defaultValues: {
      studyPlanEnrollment:
        careerOptions.length === 1 ? careerOptions[0] : undefined,
      type: undefined,
    },
  });
  const { handleSubmit, reset, watch, control, setValue } = methods;

  const typeOptions = useMemo<SelectOptionType[]>(
    () =>
      types.map((t) => ({
        value: t.id,
        label: t.name,
        paymentRequired: t.paymentRequired,
      })),
    [types],
  );

  const text = useMemo(() => {
    const prefix = 'modal';
    return {
      cancel: t(`${prefix}.newPetition.cancel`),
      save: t(`${prefix}.newPetition.save`),
      message: {
        error: t(`${prefix}.newPetition.message.error`),
        success: t(`${prefix}.newPetition.message.success`),
      },
      type: t(`${prefix}.form.type`),
      payment: t(`${prefix}.form.payment`),
      validation: {
        select: t(`${prefix}.validation.select`),
      },
    };
  }, [t]);

  const { updateQuery } = useFetch<
    PaginationQuery,
    PaginationRequestType<PetitionInf>
  >();
  const [loadingSave, setLoading] = useState(false);
  const [isFileLoading, setIsFileLoading] = useState(false);

  const handleLoadingChange = (loading: boolean) => {
    setIsFileLoading(loading);
  };

  const clearForm = useCallback(() => {
    reset({
      studyPlanEnrollment: undefined,
      cellPhone,
      email,
    });
  }, [reset, cellPhone, email]);

  const onSubmit = useCallback(
    async (data: PetitionData) => {
      setLoading(true);

      const {
        cellPhone,
        email,
        docs,
        type,
        title,
        studyPlanEnrollment,
        description,
        replaceEvaluationGrade,
        studentTestGradeId,
        studentFinalGradeId,
        section,
        grade,
        course,
      } = data;

      const petitionData = {
        email,
        title,
        description,
        docIds: docs?.map((doc) => Number(doc.id)),
        phone: cellPhone,
        typeId: type.value,
        studyPlanEnrollmentId: studyPlanEnrollment.value,
        replaceEvaluationGrade: replaceEvaluationGrade?.label,
        studentTestGradeId,
        studentFinalGradeId,
        sectionId: section?.value,
        grade,
        courseId: course?.value,
        /**
         * No se considera aun, porque el precio
         * se calcula a la hora de pagar
         */
        // payment: payment?.value,
      };

      const { error, status } = await createPetition(petitionData);

      if (status === HTTP_STATUS_CODE.CREATED) {
        addToast({
          icon: 'success',
          color: 'success',
          text: text.message.success,
        });
        close();
        clearForm();
        updateQuery({ items_per_page: 10, page: 0 });
      } else if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: text.message.error,
        });
      }
      setLoading(false);
    },
    [close, updateQuery, text, clearForm, setLoading],
  );

  const selectedType = watch('type');
  const selectedStudyPlanEnrollment = watch('studyPlanEnrollment');
  const selectedCourse = watch('course');

  useEffect(() => {
    if (selectedType) {
      setValue('replaceEvaluationGrade', undefined);
      setValue('studentTestGradeId', undefined);
      setValue('studentFinalGradeId', undefined);
      setValue('grade', undefined);
      setValue('section', undefined);
      setValue('course', undefined);
    }
  }, [selectedType, setValue]);

  useMemo(() => {
    const selectedCurrentType = types.find((e) => e.id === selectedType?.value);

    if (selectedCurrentType?.paymentRequired) {
      if (
        selectedCurrentType.paymentType === PetitionPaymentTypeId.BY_CREDIT &&
        selectedCourse?.value &&
        selectedStudyPlanEnrollment?.value
      ) {
        // Buscar el plan de estudio seleccionado
        const courses = subjectAssignmentCourses.find(
          (e) => e.studyPlanEnrollmentId === selectedStudyPlanEnrollment.value,
        );
        // Buscar el curso seleccionado en el plan de estudio
        const selectCourse = courses?.courses.find(
          (e) => e.id === selectedCourse.value,
        );

        setValue(
          'payment',
          String(selectCourse?.paymentAmount ?? 'No asignado'),
        );
        return;
      }

      if (selectedCurrentType.paymentType === PetitionPaymentTypeId.BY_FIXED) {
        setValue(
          'payment',
          String(selectedCurrentType?.paymentAmount ?? 'No asignado'),
        );
        return;
      }
    }

    setValue('payment', undefined);
  }, [
    selectedType,
    selectedStudyPlanEnrollment,
    selectedCourse,
    types,
    subjectAssignmentCourses,
    setValue,
  ]);

  if (error) {
    return <DisplayError insideCard loadingAction={loading} body={error} />;
  }
  if (loading) {
    return <Loading />;
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="mt-4 mb-1">
          <Col md={selectedType && selectedType.paymentRequired ? 6 : 12}>
            <Select
              label={text.type}
              name="type"
              control={control}
              options={typeOptions}
              rules={{ required: text.validation.select }}
              isClearable={false}
            />
          </Col>

          {selectedType && selectedType.paymentRequired && (
            <Col md={6}>
              <TextInput
                label={text.payment}
                name="payment"
                formatter={TariffFormatter}
                control={control}
                disabled={true}
              />
            </Col>
          )}

          {selectedType &&
            (selectedType.value === PetitionTypes.ACADEMIC ||
              selectedType.value === PetitionTypes.PAYMENT ||
              selectedType.value === PetitionTypes.OTHER) && (
              <PetitionFormDefault
                careerOptions={careerOptions}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.REPLACE_EVALUATION_GRADE && (
              <PetitionFormReplaceEvaluationGrade
                careerOptions={careerOptions}
                replaceEvaluationGrades={replaceEvaluationGrades}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType &&
            selectedType.value === PetitionTypes.REPLACE_COURSE_GRADE && (
              <PetitionFormReplaceCourseGrade
                careerOptions={careerOptions}
                sectionData={replaceCourseGrades}
                onLoadingChange={handleLoadingChange}
              />
            )}

          {selectedType && selectedType.value === PetitionTypes.PROFICIENCY && (
            <PetitionFormProficiency
              careerOptions={careerOptions}
              proficiencyCourses={proficiencyCourses}
              onLoadingChange={handleLoadingChange}
            />
          )}

          {selectedType && selectedType.value === PetitionTypes.VALIDATION && (
            <PetitionFormValidation
              careerOptions={careerOptions}
              // se usan los mismos datos que los de proficiency
              validationCourses={proficiencyCourses}
              onLoadingChange={handleLoadingChange}
            />
          )}

          {selectedType &&
            selectedType.value === PetitionTypes.SUBJECT_ASSIGNMENT && (
              <PetitionFormSubjectAssignment
                careerOptions={careerOptions}
                subjectAssignmentCourses={subjectAssignmentCourses}
                onLoadingChange={handleLoadingChange}
              />
            )}

          <Col md={6}>
            <Button
              type="button"
              className="w-100"
              outlined
              loading={loadingSave}
              onClick={() => {
                close();
                clearForm();
              }}
              text={text.cancel}
            />
          </Col>
          <Col md={6}>
            <Button
              type="submit"
              className="w-100"
              color="primary"
              loading={loadingSave}
              disabled={isFileLoading}
              text={text.save}
            />
          </Col>
        </Row>
      </Form>
    </FormProvider>
  );
}
